import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useFirebasePhoneAuth } from '../hooks/useFirebasePhoneAuth';
import { useTranslation } from 'react-i18next';

interface PhoneVerificationProps {
  onVerificationComplete: (phoneNumber: string) => void;
  onCancel?: () => void;
  className?: string;
  initialPhoneNumber?: string;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({ 
  onVerificationComplete,
  onCancel,
  className = '',
  initialPhoneNumber = ''
}) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [countdown, setCountdown] = useState(0);
  const [showRecaptcha, setShowRecaptcha] = useState(true);
  const initializedRef = useRef(false);
  
  const { 
    loading, 
    error, 
    sendVerificationCode, 
    verifyCode, 
    reset,
    initializeRecaptcha
  } = useFirebasePhoneAuth();

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Initialize reCAPTCHA on mount (only once)
  useEffect(() => {
    if (initializedRef.current) {
      console.log('PhoneVerification: reCAPTCHA already initialized, skipping...');
      return;
    }
    
    console.log('PhoneVerification: Initializing reCAPTCHA');
    console.log('Container element:', document.getElementById('recaptcha-container'));
    
    // Test if container exists
    const container = document.getElementById('recaptcha-container');
    if (!container) {
      console.error(t('phoneVerification.recaptchaContainerNotFound'));
      return;
    }
    
    console.log('Container found, initializing reCAPTCHA...');
    initializedRef.current = true;
    
    initializeRecaptcha('recaptcha-container').catch(error => {
      console.error(t('phoneVerification.failedToInitialize'), error);
      initializedRef.current = false; // Reset on error
    });
  }, [initializeRecaptcha]);

  // Update phone number when initialPhoneNumber changes
  useEffect(() => {
    if (initialPhoneNumber) {
      setPhoneNumber(initialPhoneNumber);
    }
  }, [initialPhoneNumber]);

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      return;
    }

    const result = await sendVerificationCode(phoneNumber.trim());
    if (result.success) {
      setStep('code');
      setCountdown(60); // 60 second countdown for resend
      setShowRecaptcha(false); // Hide reCAPTCHA after successful code sending
      // Clear reCAPTCHA after successful code sending
      reset();
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      return;
    }

    const result = await verifyCode(verificationCode.trim());
    if (result.success && result.idToken) {
      // Send to backend for verification
      try {
        const response = await axios.post('/api/phone-auth/verify', {
          firebaseIdToken: result.idToken
        }
      );

        const data = response.data;
        if (data.success) {
          onVerificationComplete(phoneNumber);
        } else {
          console.error(t('phoneVerification.backendVerificationFailed'), data.error);
          // Don't show alert, let the hook handle the error
        }
      } catch (error) {
        console.error(t('phoneVerification.backendVerificationFailed'), error);
        // Don't show alert, let the hook handle the error
      }
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    const result = await sendVerificationCode(phoneNumber.trim());
    if (result.success) {
      setCountdown(60);
    }
  };

  const handleReset = () => {
    reset();
    setStep('phone');
    setPhoneNumber('');
    setVerificationCode('');
    setCountdown(0);
    setShowRecaptcha(true); // Show reCAPTCHA again when resetting
  };

  const handleCancel = () => {
    reset();
    setShowRecaptcha(true); // Show reCAPTCHA again when canceling
    if (onCancel) {
      onCancel();
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters except +
    const cleaned = value.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, assume it's a US number
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('1') && cleaned.length === 11) {
        return '+' + cleaned;
      } else if (cleaned.length === 10) {
        return '+1' + cleaned;
      } else {
        return '+1' + cleaned;
      }
    }
    
    return cleaned;
  };

  return (
    <div className={`phone-verification ${className}`}>
      {/* reCAPTCHA container - only show when needed */}
      {showRecaptcha && (
        <div id="recaptcha-container" className="flex justify-center mb-4 min-h-[78px] border-2 border-dashed border-gray-300 rounded-lg p-4">
          {/* <div className="text-center text-gray-500">
            <div className="text-sm font-medium mb-2">reCAPTCHA Widget</div>
            <div className="text-xs" id="recaptcha-status">
              {error && error.includes('auth/argument-error') 
                ? 'Development Mode: Mock reCAPTCHA Active' 
                : 'Loading...'
              }
            </div>
            {import.meta.env.DEV && (
              <div className="text-xs text-blue-500 mt-2">
                Development Mode: Using test phone numbers
              </div>
            )}
          </div> */}
        </div>
      )}
      
      {error && (
        <div className="error-message bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {step === 'phone' && (
        <div className="phone-step">
          <h3 className="text-xl font-semibold mb-4">
            {t('phoneVerification.enterPhoneNumber')}
          </h3>
          <p className="text-gray-600 mb-4">
            {initialPhoneNumber 
              ? t('phoneVerification.phoneDescriptionWithNumber', 'We found a phone number in your profile. We\'ll send you a verification code to verify this number.')
              : t('phoneVerification.phoneDescription')
            }
          </p>
          
          <div className="mb-4">
            <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-2">
              {t('phoneVerification.phoneNumber')}
            </label>
            <input
              id="phone-number"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
              placeholder={t('phoneVerification.phoneNumberPlaceholder')}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          <div className="flex gap-3">
            <button 
              onClick={handleSendCode}
              disabled={loading || !phoneNumber.trim()}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? t('phoneVerification.sending') : t('phoneVerification.sendCode')}
            </button>
            
            {onCancel && (
              <button 
                onClick={handleCancel}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:bg-gray-100"
              >
                {t('common.cancel')}
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'code' && (
        <div className="code-step">
          <h3 className="text-xl font-semibold mb-4">
            {t('phoneVerification.enterCode')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('phoneVerification.codeDescription', { phoneNumber })}
          </p>
          
          <div className="mb-4">
            <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-2">
              {t('phoneVerification.verificationCode')}
            </label>
            <input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              placeholder={t('phoneVerification.verificationCodePlaceholder')}
              maxLength={6}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 text-center text-lg tracking-widest"
            />
          </div>

          <div className="flex gap-3 mb-4">
            <button 
              onClick={handleVerifyCode}
              disabled={loading || !verificationCode.trim()}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? t('phoneVerification.verifying') : t('phoneVerification.verifyCode')}
            </button>
          </div>

          <div className="flex gap-3">
            <button 
              onClick={handleResendCode}
              disabled={loading || countdown > 0}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:bg-gray-100"
            >
              {countdown > 0 
                ? t('phoneVerification.resendIn', { seconds: countdown })
                : t('phoneVerification.resendCode')
              }
            </button>
            
            <button 
              onClick={handleReset}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:bg-gray-100"
            >
              {t('phoneVerification.changePhone')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 